import { useState, useCallback, useRef } from 'react';

interface RetryOptions {
  maxAttempts?: number;
  delay?: number;
  backoff?: 'linear' | 'exponential';
  onRetry?: (attemptNumber: number) => void;
  retryCondition?: (error: any) => boolean;
}

interface RetryState {
  attempts: number;
  isRetrying: boolean;
  lastError: Error | null;
}

export const useRetry = () => {
  const [state, setState] = useState<RetryState>({
    attempts: 0,
    isRetrying: false,
    lastError: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const calculateDelay = (
    attemptNumber: number,
    baseDelay: number,
    backoff: 'linear' | 'exponential'
  ): number => {
    if (backoff === 'exponential') {
      return baseDelay * Math.pow(2, attemptNumber - 1);
    }
    return baseDelay * attemptNumber;
  };

  const retry = useCallback(
    async <T,>(
      fn: (signal?: AbortSignal) => Promise<T>,
      options: RetryOptions = {}
    ): Promise<T> => {
      const {
        maxAttempts = 3,
        delay = 1000,
        backoff = 'exponential',
        onRetry,
        retryCondition = () => true,
      } = options;

      // Cancel any ongoing retry
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      setState({ attempts: 0, isRetrying: false, lastError: null });

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          setState((prev) => ({ ...prev, attempts: attempt, isRetrying: true }));

          const result = await fn(signal);

          setState((prev) => ({ ...prev, isRetrying: false }));
          return result;
        } catch (error: any) {
          setState((prev) => ({ ...prev, lastError: error }));

          // Check if we should abort
          if (signal.aborted) {
            throw new Error('Retry cancelled', { cause: error });
          }

          // Check if this is the last attempt
          if (attempt === maxAttempts) {
            setState((prev) => ({ ...prev, isRetrying: false }));
            throw error;
          }

          // Check retry condition
          if (!retryCondition(error)) {
            setState((prev) => ({ ...prev, isRetrying: false }));
            throw error;
          }

          // Call onRetry callback if provided
          if (onRetry) {
            onRetry(attempt);
          }

          // Calculate and apply delay
          const waitTime = calculateDelay(attempt, delay, backoff);
          await new Promise((resolve) => setTimeout(resolve, waitTime));
        }
      }

      // This should never be reached, but TypeScript needs it
      throw new Error('Max retry attempts reached');
    },
    []
  );

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setState((prev) => ({ ...prev, isRetrying: false }));
    }
  }, []);

  const reset = useCallback(() => {
    setState({
      attempts: 0,
      isRetrying: false,
      lastError: null,
    });
  }, []);

  return {
    retry,
    cancel,
    reset,
    ...state,
  };
};
